<template>
  <Dialog @keydown.esc.native="handleCloseDialog()">
    <div class="flex flex-col w-full h-full">
      <div id="header">
        <div class="flex justify-between items-center pl-5 pr-4 pt-3.5">
          <div class="flex items-center space-x-5">
            <div class="flex items-center space-x-2 font-manrope cursor-default">
              <img :src="`/static/roundtable_icons/${'verbal'}_icon_color.svg`" class="w-4 h-4" />
              <span>Verbal RoundTable</span>
            </div>
            <button v-if="isLoading === false && course" class="flex items-center space-x-2.5 font-manrope text-gray-800">
              <div class="flex items-center space-x-2.5 hover:underline">
                <img src="/static/roundtable_icons/courses_icon.svg" class=" w-3 -mt-0.5" />
                <span >{{ course.title }}</span>
              </div>
            </button>
          </div>
          <div class="flex items-center space-x-3">
            <div v-if="isSaving" class="flex items-center space-x-2">
              <div class="flex items-center justify-center">
                <img src="/static/roundtable_icons/cloud-upload.svg" class="w-4 h-4"/>
              </div>
              <span class="font-manrope text-xs text-gray-800">Saving Changes...</span>
            </div>
            <button @click="handleCloseDialog()" class="w-7 h-7 focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark rounded-full cursor-pointer bg-gray-50 hover:bg-gray-100 text-base flex items-center justify-center hover:text-gray-800 text-gray-700">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="flex justify-between space-x-5 items-center px-5 relative w-full mb-2.5 mt-2.5">
          <div class="flex-grow">
            <input
              v-model="title"
              placeholder="Enter a title..."
              class="w-full text-3xl font-manrope font-bold text-gray-800 border-none bg-white py-1 outline-none"
            />
          </div>
          <div class="flex items-center space-x-2">
            <button @click="handleSaveRoundTable()" :disabled="isSaveButtonDisabled" class="flex sample items-center space-x-2 disabled:opacity-50 bg-primary hover:bg-primary-dark rounded-md border border-solid border-primary text-white py-1.5 px-4">
              <div v-if="isSaving === true" class="loader -ml-0.5"></div>
              <span>{{ isSaving === true ? 'Saving...' : 'Save' }}</span>
            </button>
          </div>
        </div>
        <div class="flex items-center justify-between w-full bg-white h-11 focus-within:border-pinky border-b  border-solid border-gray-200 pl-4 pr-5">
          <div class="flex items-center w-full font-manrope">
            <div class="w-14 h-14 -ml-3">
              <iframe src="https://lottie.host/embed/874b8bf3-ca7e-46f3-9c81-cf94974ad30b/Djg5qZsQK9.lottie" width="100%" height="100%"></iframe>
            </div>
            <input 
              ref="genieRevise" 
              @keyup.enter="handleReviseVerbalPrompt()" 
              v-model="revise.input" placeholder="How can I help you make this discussion prompt better?" 
              class="w-full disabled:bg-white border-none"
            />
          </div>
          <div class="flex items-center space-x-5">
            <pl-menu direction="right" :menuSize="3">
              <button slot="menu_trigger" class="flex items-center space-x-2.5 font-manrope text-gray-800">
                <span>{{ revise.mode }}</span>
                <i class="fa-solid fa-chevron-down text-xs text-gray-600 hover:text-gray-800"></i>
              </button>
              <ul class="flex flex-col space-y-1.5 rounded-lg mt-0.5 text-sm shadow-sm px-1 pt-0.5 pb-1 font-manrope max-h-96" menu-content>
                <li class="flex items-center space-x-1 justify-between hover:bg-gray-100 px-2.5 py-1 cursor-pointer rounded-md">
                  <span>Prompt</span>
                  <i v-if="revise.mode === 'Prompt'" class="fa-solid fa-check text-gray-400"></i>
                </li>
              </ul>
            </pl-menu>
            <button @click="handleReviseVerbalPrompt()" :disabled="revise.input === '' || revise.input.length < 4 ? true : false" class="text-white w-8 h-6 rounded-lg flex justify-center items-center disabled:opacity-50 bg-pinky hover:bg-pinky-dark cursor-pointer">
              <i class="fa-solid fa-arrow-right-long"></i>
            </button>
          </div>
        </div>
        <div v-if="isRevising === true" class='w-full'>
          <div class='h-0.5 w-full bg-pink-100 overflow-hidden'>
            <div class='progress w-full h-full bg-pink-500 left-right'></div>
          </div>
        </div>
      </div>
      <div class="w-full h-full">
        <div id="toolbar-container" class="border-b border-solid border-gray-200"></div>
        <div class="overflow-y-auto w-full h-full pb-60">
          <div class="relative">
            <Editor
              api-key="no-api-key"
              :init="editor"
              v-model="instructions"
              :inline="true"
              tag-name="div"
            />
          </div>
          <div class="flex flex-col space-y-5 font-manrope mb-8 px-16">
            <h3 class="text-2xl text-gray-800 font-bold">
              Content & Multimedia
            </h3>
            <div v-if="isLoading === false" class="flex items-center space-x-2 w-full">
              <input 
                class="w-full bg-gray-100 border border-solid rounded-md border-gray-200 py-1.5 px-3.5 focus-within:ring-2 ring-offset-2 ring-primary" 
                v-model="resource.name" 
                placeholder="Enter a Resource Name..."
              />
              <input 
                class="w-full bg-gray-100 border border-solid rounded-md border-gray-200 py-1.5 px-3.5 focus-within:ring-2 ring-offset-2 ring-primary" 
                v-model="resource.url"
                @keyup.enter="handleAddResource()" 
                placeholder="Enter a Resource URL..."
              />
              <button @click="handleAddResource()" :disabled="resource.name === '' && resource.url === '' ? true : false" class="disabled:opacity-50 bg-blueGray-800 rounded-md px-4 py-1.5 text-white">Add</button>
            </div>
            <div v-if="roundtable && resources.length > 0 && isLoading === false" class="w-full">
              <ul class="flex flex-col">
                <Draggable
                  v-model="resources"
                  @start="isDragResource = true"
                  @end="isDragResource = false"
                  handle=".handle"
                  class="h-full flex-grow overflow-y-auto"
                >
                  <li v-for="(r, index) in resources" :key="index" class="flex items-center justify-between space-x-2 text-base mb-1 text-blue-600">
                    <div class="flex items-center space-x-2">
                      <button class="h-8 w-8 hover:bg-gray-100 rounded-full handle">
                        <i class="fa-solid fa-grip-vertical text-blueGray-600"></i>
                      </button>
                      <a :href="r.resourceURL" target="_blank" class="hover:underline">{{ r.resourceName }}</a>
                      <i class="fa-solid fa-arrow-up-right-from-square text-blue-500" style="font-size:11px"></i>
                    </div>
                    <div class="flex items-center space-x-2">
                      <div 
                        @click="handleChangeResourceStatus(r.id ? r.id : index)"
                        title="Hide Resource" 
                        class="w-8 cursor-pointer h-5 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out"
                        :class="{ 'bg-primary': r.is_active }"
                      >
                        <div 
                          class="bg-white w-3 h-3 rounded-full transform duration-300 ease-in-out"
                          :class="{ 'translate-x-3': r.is_active }"
                          >
                        </div>
                      </div>
                      <button @click="handleDeleteResource(r.id ? r.id : index)" title="Remove" class="flex items-center justify-center w-7 h-7 bg-gray-100 hover:bg-red-100 rounded-full text-red-500">
                        <i class="fa-solid fa-square-minus text-xs"></i>
                      </button>
                    </div>
                  </li>
                </Draggable>
              </ul>
            </div>
            <div v-if="isLoading === true">
              <div class="flex flex-col space-y-2.5 w-full animate-pulse">
                <div class="h-3.5 bg-gray-200 rounded-full dark:bg-gray-700 w-6/12"></div>
                <div class="h-3.5 bg-gray-300 rounded-full dark:bg-gray-700 w-8/12"></div>
                <div class="h-3.5 bg-gray-200 rounded-full dark:bg-gray-700 w-5/12"></div>
              </div>
            </div>
          </div>
          <div class="flex flex-col space-y-5 font-manrope px-16">
            <div class="flex items-center justify-between">
              <h3 class="text-2xl text-gray-800 font-bold">
                Questions
              </h3>
            </div>
            <div v-if="isLoading === false" class="flex items-center space-x-2 w-full">
              <input 
                class="w-full bg-gray-100 border border-solid rounded-md border-gray-200 py-1.5 px-3.5 focus-within:ring-2 ring-offset-2 ring-primary" 
                v-model="question.question" 
                placeholder="Enter a Question..."
                @keyup.enter="handleAddQuestion()"
              />
              <button @click="handleAddQuestion()" :disabled="question.question === '' ? true : false" class="bg-blueGray-800 disabled:opacity-50 rounded-md px-4 py-1.5 text-white">Add</button>
            </div>
            <div v-if="roundtable && questions.length > 0 && isLoading === false" class="w-full">
              <ul class="flex flex-col">
                <Draggable
                  v-model="questions"
                  @start="isDragQuestion = true"
                  @end="isDragQuestion = false"
                  handle=".handle-question"
                  class="h-full flex-grow overflow-y-auto"
                >
                  <li v-for="(q, index) in questions" class="flex justify-between items-center space-x-2 text-base mb-1.5">
                    <div class="flex items-center space-x-2 w-full">
                      <button class="h-8 w-8 hover:bg-gray-100 focus-within:outline-none rounded-full handle-question cursor-move">
                        <i class="fa-solid fa-grip-vertical text-blueGray-600"></i>
                      </button>
                      <input v-model="q.question" class="w-full border-none rounded"></input>
                    </div>
                    <div class="flex items-center space-x-3">
                      <div 
                        @click="handleChangeQuestionStatus(index)"
                        title="Hide Resource" 
                        class="w-8 cursor-pointer h-5 flex items-center bg-gray-200 rounded-full p-1 duration-300 ease-in-out"
                        :class="{ 'bg-primary': q.is_active }"
                      >
                        <div 
                          class="bg-white w-3 h-3 rounded-full transform duration-300 ease-in-out"
                          :class="{ 'translate-x-3': q.is_active }"
                          >
                        </div>
                      </div>
                      <button @click="handleDeleteQuestion(index)" title="Remove" class="flex items-center justify-center w-7 h-7 bg-gray-100 hover:bg-red-100 focus-within:border-red-500 focus-within:border focus-within:border-solid focus-within:outline-none rounded-full text-red-500">
                        <i class="fa-solid fa-square-minus text-xs"></i>
                      </button>
                    </div>
                  </li>
                </Draggable>
              </ul>
            </div>
            <div v-if="isLoading === true">
              <div class="flex flex-col space-y-2.5 w-full animate-pulse">
                <div class="h-3.5 bg-gray-200 rounded-full dark:bg-gray-700 w-6/12"></div>
                <div class="h-3.5 bg-gray-300 rounded-full dark:bg-gray-700 w-10/12"></div>
                <div class="h-3.5 bg-gray-200 rounded-full dark:bg-gray-700 w-6/12"></div>
                <div class="h-3.5 bg-gray-300 rounded-full dark:bg-gray-700 w-5/12"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
<script>
  import Axios from 'axios';
  import Dialog from '../../../components/Dialogs/Dialog.vue';
  import Editor from '@tinymce/tinymce-vue';
  import { EditorVerbalOptions } from '../../Drafts/EditorVerbalOptions.js';
  import { mapGetters } from 'vuex';
  import { EventBus } from '../../../utils/event-bus';
  import Draggable from 'vuedraggable';
  import _ from 'lodash';
  export default {
    name: "EditVerbalRoundTable",
    components: {
      Dialog,
      Editor,
      Draggable
    },
    props: {
      id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        title: "",
        instructions: "",
        questions: [],
        resources: [],
        revise: {
          input: "",
          mode: "Prompt"
        },
        isSaving: false,
        isLoading: false,
        isDragResource: false,
        isDragQuestion: false,
        isRevising: false,
        resource: { name: "", url: "", is_active: null, },
        question: { question: "", is_custom: null, is_active: null },
        editor: EditorVerbalOptions,
        roundtable: null,
        course: null,
      };
    },
    computed: {
      ...mapGetters(['getProfile', 'getCurrentDiscussion', 'getCourseDetail', 'getCourses', 'getRecentRtsList']),
      isSaveButtonDisabled() {
        const discussion = this.getCurrentDiscussion;
        if (!discussion || !this.instructions) return true;

        const unchangedTitle = discussion.title === this.title;
        const unchangedInstructions = discussion.instructions === this.instructions;
        const unchangedQuestions = _.isEqual(discussion.toggleable_guiding_questions, this.questions);
        const unchangedResources = _.isEqual(discussion.toggleable_resource_questions, this.resources);

        return unchangedTitle && unchangedInstructions && unchangedQuestions && unchangedResources;
      }
    },
    methods: {
      handleCloseDialog: async function() {
        this.$emit('close');
      },
      handleChangeInstructions: function(event) {
        this.instructions = event.target.innerText;
      },
      handleAddResource: function() {
        if (this.resource.name === '' || this.resource.url === '') {
          EventBus.$emit('createToast', {type: "Error", message: "You need to set name and a valid url to add a resource", time: 5000});
          return;
        }
        this.resources.push({ id: this.resources.length, resourceName: this.resource.name, resourceURL: this.resource.url, is_active: true });
        this.resource = { name: "", url: "", is_active: null };
      },
      handleDeleteResource: function(id) {
        const indexToDelete = this.resources.findIndex(item => item.id === id);
        if (indexToDelete !== -1) {
          this.resources.splice(indexToDelete, 1);
        }
      },
      handleChangeResourceStatus: function(id) {
        const resource_object = this.resources.find((obj, index) => index === id);
        this.resources.find((obj, index) => index === id).is_active = !resource_object.is_active;
      },
      handleAddQuestion: function() {
        if (this.question.question === '') {
          return;
        }
        this.questions.push({id: this.questions.length, question: this.question.question, is_active: true, is_custom: true });
        this.question = { question: "", is_active: null, is_custom: null };
      },
      handleDeleteQuestion: function(index) {
        if (index >= 0 && index < this.questions.length) {
          this.questions.splice(index, 1);
        }
      },
      handleChangeQuestionStatus: function(index) {
        const question_obj = this.questions[index];
        if (question_obj) {
          question_obj.is_active = !question_obj.is_active;
        }
      },
      handleSaveRoundTable: async function() {
        try {
          this.isSaving = true;
          await this.$store.dispatch('saveLiveDiscussionSettings', {
            id: this.roundtable.id,
            title: this.title,
            instructions: this.instructions,
            toggleable_guiding_questions: this.questions,
            toggleable_resource_questions: this.resources,
          });
          this.$emit('close');
          EventBus.$emit('createToast', {type: "Success", message: "Verbal RoundTable updated successfully!", time: 5000 });
          this.isSaving = false;
        } catch (error) {
          console.error(error);
          EventBus.$emit('createToast', {type: "Error", message: "Something happened updating discussion, please try again!", time: 5000 });
        }
      },
      handleGetData: async function() {
        this.isLoading = true;
        try {
          if (this.$route.name === 'dashboard') {
            this.title = this.getRecentRtsList.find(rt => rt.id === this.id).title;
          }
          if (this.getCurrentDiscussion.id !== this.id) {
            await this.$store.dispatch('fetchLiveDiscussion', this.id);
          }
          this.roundtable = { ...this.getCurrentDiscussion };
          this.title = this.roundtable.title;
          this.course = (this.getCourses.length === 0) ? this.getCourseDetail : this.getCourses.find(course => course.id === this.roundtable.CourseId);
          this.instructions = this.roundtable.instructions;
          this.questions = this.roundtable.toggleable_guiding_questions.map(q => ({ ...q }));
          this.resources = this.roundtable.toggleable_resource_questions.map(r => ({ ...r }));
        } catch (error) {
          console.error(error);
        } finally {
          this.isLoading = false;
        }
      },
      handleReviseVerbalPrompt: async function() {
        try {
          if (this.isRevising === true) {
            return;
          }
          this.isRevising = true;
          const payload = {
            input: this.revise.input,
            instructions: this.instructions,
            questions: this.questions,
          };
          this.revise.input = 'Thinking...';
          this.$refs.genieRevise.blur();
          const response = await Axios.post(`/api/genie/v1/revise/verbal/prompt`, payload);
          this.questions = response.data.questions;
          this.instructions = response.data.instructions;
          
          this.isRevising = false;
          this.revise.input = '';
          this.revise.mode = 'Prompt';

        } catch (error) {
          console.error(error);
        }
      },
    },
    mounted: async function() {
      await this.handleGetData();
    }
  }; 
</script>
<style scoped>

  .progress {
    animation: progress 1s infinite linear;
  }

  .left-right {
    transform-origin: 0% 50%;
  }

  @keyframes progress {
    0% {
      transform:  translateX(0) scaleX(0);
    }
    40% {
      transform:  translateX(0) scaleX(0.4);
    }
    100% {
      transform:  translateX(100%) scaleX(0.5);
    }
  }
  
  .loader,
  .loader:after {
    border-radius: 50%;
    width: 16px;
    height: 16px;
    margin-top: -1px;
  }
  .loader {
    border-top: 3px solid rgba(255, 255, 255, 0.2);
    border-right: 3px solid rgba(255, 255, 255, 0.2);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
</style>


// WEBPACK FOOTER //
// EditVerbalRoundTable.vue?696712c6